/* TrackAuditForm — user enters a track's metadata (Title, ISRC, ISWC,
 * songwriters + splits, etc.). Submits to POST /api/royai/audit/tracks,
 * which validates + persists + returns any issues so the audit score
 * refreshes live.
 */
import { useState } from "react";
import { Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { royai } from "@/lib/royai";

const emptySplit = () => ({ name: "", percentage: "", ipi: "", role: "songwriter" });

const fieldClass =
  "w-full bg-black border border-white/10 px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none placeholder:text-neutral-600";

export default function TrackAuditForm({ onAdded, existingTracks = [] }) {
  const [track, setTrack] = useState({
    title: "", isrc: "", iswc: "", label: "",
    artist_credit: "", recording_ownership: "",
    splits: [emptySplit(), emptySplit()],
  });
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const set = (k, v) => setTrack((t) => ({ ...t, [k]: v }));
  const setSplit = (i, k, v) =>
    setTrack((t) => {
      const arr = [...t.splits];
      arr[i] = { ...arr[i], [k]: v };
      return { ...t, splits: arr };
    });
  const addSplit = () => setTrack((t) => ({ ...t, splits: [...t.splits, emptySplit()] }));
  const removeSplit = (i) => setTrack((t) => ({ ...t, splits: t.splits.filter((_, x) => x !== i) }));

  const splitsTotal = track.splits.reduce(
    (s, sp) => s + (parseFloat(sp.percentage) || 0), 0
  );
  const splitsValid = Math.abs(splitsTotal - 100) < 0.01;

  async function submit(e) {
    e.preventDefault();
    if (!track.title.trim()) { toast.error("Title is required"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...track,
        splits: track.splits
          .filter((s) => (s.name || "").trim() || s.percentage)
          .map((s) => ({
            name: s.name.trim(),
            percentage: parseFloat(s.percentage) || 0,
            ipi: s.ipi?.trim() || null,
            role: s.role || null,
          })),
      };
      const r = await royai.addTrack(payload);
      setLastResult(r);
      if (r.issues.length === 0) {
        toast.success(`"${track.title}" added — no issues found.`);
      } else {
        toast.warning(`"${track.title}" added — ${r.issues.length} issue(s) flagged.`);
      }
      setTrack({
        title: "", isrc: "", iswc: "", label: "",
        artist_credit: "", recording_ownership: "",
        splits: [emptySplit(), emptySplit()],
      });
      onAdded && await onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add track");
    }
    setSubmitting(false);
  }

  async function deleteTrack(id, title) {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await royai.deleteTrack(id);
      toast.success("Track deleted");
      onAdded && await onAdded();
    } catch { toast.error("Failed to delete"); }
  }

  return (
    <div className="border border-white/5 bg-black/40 p-5 lg:p-6" data-testid="track-audit-form">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-display text-lg font-medium">Add Track to Audit</h4>
        <span className="text-xs font-mono-tech uppercase tracking-widest text-neutral-500">
          Real-time validation
        </span>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400 mb-1">Title *</div>
            <input className={fieldClass} value={track.title} onChange={(e) => set("title", e.target.value)} placeholder="Golden Hour" data-testid="track-title" />
          </label>
          <label className="block">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400 mb-1">ISRC</div>
            <input className={fieldClass} value={track.isrc} onChange={(e) => set("isrc", e.target.value)} placeholder="USRC17607839" data-testid="track-isrc" />
          </label>
          <label className="block">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400 mb-1">ISWC</div>
            <input className={fieldClass} value={track.iswc} onChange={(e) => set("iswc", e.target.value)} placeholder="T3452786132" data-testid="track-iswc" />
          </label>
          <label className="block">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400 mb-1">Artist Credit</div>
            <input className={fieldClass} value={track.artist_credit} onChange={(e) => set("artist_credit", e.target.value)} placeholder="Only Martian" data-testid="track-artist" />
          </label>
          <label className="block">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400 mb-1">Label</div>
            <input className={fieldClass} value={track.label} onChange={(e) => set("label", e.target.value)} placeholder="OFGS" data-testid="track-label" />
          </label>
          <label className="block">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400 mb-1">Recording Ownership</div>
            <input className={fieldClass} value={track.recording_ownership} onChange={(e) => set("recording_ownership", e.target.value)} placeholder="Only Few Get Selected LLC" data-testid="track-ownership" />
          </label>
        </div>

        {/* Splits */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400">Songwriter / Publishing Splits *</div>
            <div className={`text-xs font-mono-tech tracking-widest ${splitsValid ? "text-emerald-400" : "text-rose-400"}`} data-testid="splits-total">
              TOTAL {splitsTotal.toFixed(2)}% {splitsValid ? "✓" : "≠ 100%"}
            </div>
          </div>
          <div className="space-y-2">
            {track.splits.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_90px_130px_auto] gap-2 items-center" data-testid={`split-row-${i}`}>
                <input className={fieldClass} placeholder="Songwriter name" value={s.name} onChange={(e) => setSplit(i, "name", e.target.value)} />
                <input className={fieldClass} type="number" step="0.01" placeholder="%" value={s.percentage} onChange={(e) => setSplit(i, "percentage", e.target.value)} />
                <input className={fieldClass} placeholder="IPI (optional)" value={s.ipi} onChange={(e) => setSplit(i, "ipi", e.target.value)} />
                <button type="button" onClick={() => removeSplit(i)} className="text-neutral-500 hover:text-rose-400 p-2" aria-label="Remove split">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addSplit} className="mt-2 text-xs font-mono-tech uppercase tracking-widest text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid="add-split">
            <Plus size={12} /> Add Split
          </button>
        </div>

        <div className="flex items-center gap-3 pt-2 border-t border-white/5">
          <button type="submit" disabled={submitting}
            className="bg-amber-500 text-black px-5 py-2.5 text-sm font-mono-tech uppercase tracking-widest hover:brightness-110 disabled:opacity-50 flex items-center gap-2"
            data-testid="submit-track">
            <Plus size={14} /> {submitting ? "Auditing…" : "Add & Audit Track"}
          </button>
          <span className="text-xs text-neutral-500">
            Splits must total 100%. Missing fields drop your audit score.
          </span>
        </div>
      </form>

      {/* Live feedback for last submission */}
      {lastResult && lastResult.issues.length > 0 && (
        <div className="mt-4 border border-rose-500/30 bg-rose-500/5 p-3" data-testid="last-issues">
          <div className="text-xs font-mono-tech uppercase tracking-widest text-rose-300 mb-2">
            {lastResult.issues.length} issue(s) flagged on last submission
          </div>
          <ul className="space-y-1 text-xs text-neutral-300">
            {lastResult.issues.map((i, k) => (
              <li key={k}><span className="text-amber-400">{i.field}:</span> {i.fix}</li>
            ))}
          </ul>
        </div>
      )}

      {/* User-entered tracks list */}
      {existingTracks.length > 0 && (
        <div className="mt-5 pt-5 border-t border-white/5">
          <div className="text-xs font-mono-tech uppercase tracking-widest text-neutral-400 mb-2">
            Your Audited Tracks ({existingTracks.length})
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="user-tracks-list">
            {existingTracks.map((t) => (
              <div key={t.id} className="flex items-center justify-between border border-white/5 bg-black/40 px-3 py-2 text-xs">
                <div className="flex-1 min-w-0 truncate">
                  <span className="text-white">{t.title}</span>
                  {t.isrc && <span className="text-neutral-500"> · {t.isrc}</span>}
                </div>
                <button onClick={() => deleteTrack(t.id, t.title)} className="text-neutral-500 hover:text-rose-400 p-1 ml-2" aria-label="Delete track" data-testid={`delete-track-${t.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
